import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface DocsContentProps {
  children: React.ReactNode
  title: string
  description?: string
  previousPage?: { title: string; href: string }
  nextPage?: { title: string; href: string }
}

export function DocsContent({
  children,
  title,
  description,
  previousPage,
  nextPage,
}: DocsContentProps) {
  const navigate = useNavigate()

  const handleNavigation = (href: string) => {
    navigate(href)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <article className="docs-content wrap-break-word md:wrap-normal">
      {/* Page Header */}
      <header className="mb-8 md:mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{title}</h1>
        {description && (
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            {description}
          </p>
        )}
      </header>

      {/* Content. Phone: the text column of a bullet row may shrink below its
          longest token, so break-word can wrap it instead of widening the page. */}
      <div className="prose-docs max-md:[&_li>span:last-child:not(:first-child)]:min-w-0">
        {children}
      </div>

      {/* Previous/Next Navigation */}
      {(previousPage || nextPage) && (
        <nav className="mt-16 pt-8 border-t border-border flex items-center justify-between gap-4">
          {previousPage ? (
            <button
              onClick={() => handleNavigation(previousPage.href)}
              className="group flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-left"
            >
              <ChevronLeft className="h-4 w-4 shrink-0 group-hover:-translate-x-1 transition-transform" />
              <div className="text-left md:text-right">
                <div className="text-xs text-muted-foreground mb-1">Previous</div>
                <div className="text-sm font-medium">{previousPage.title}</div>
              </div>
            </button>
          ) : (
            <div />
          )}
          {nextPage ? (
            <button
              onClick={() => handleNavigation(nextPage.href)}
              className="group flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-left"
            >
              <div className="text-right md:text-left">
                <div className="text-xs text-muted-foreground mb-1">Next</div>
                <div className="text-sm font-medium">{nextPage.title}</div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 group-hover:translate-x-1 transition-transform" />
            </button>
          ) : (
            <div />
          )}
        </nav>
      )}
    </article>
  )
}
